import React, { useState, useMemo, memo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { CommunityResource } from '@/types/residents/communityResource';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { ResourceIcon } from './ResourceIcon';

interface ResourceTileProps {
  item: CommunityResource;
  onPress: (item: CommunityResource) => void;
}

const ResourceTileBase: React.FC<ResourceTileProps> = ({ item, onPress }) => {
  const [hovered, setHovered] = useState(false);

  const totalItems = useMemo(() => (
    [
      ...(item.files || []),
      ...(item.url ? [item.url] : []),
      ...(item.content ? [item.content] : []),
    ].length
  ), [item]);

  return (
    <View style={{ flex: 1 }}>
      <Pressable
        style={[styles.tile, hovered && styles.tileHovered]}
        onPress={() => onPress(item)}
        accessibilityRole="button"
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
      >
        <View style={styles.tileIconRow}>
          <View style={styles.tileIconCircle}>
            <ResourceIcon iconKey={item.icon || item.iconName} size={18} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />
          </View>
        </View>

        <View style={styles.tileTitleRow}>
          <Text style={styles.tileTitle} numberOfLines={1}>{item.name || item.title}</Text>
          {totalItems > 0 && (
            <View style={styles.countPill}>
              <Text style={styles.countPillText}>{totalItems}</Text>
            </View>
          )}
        </View>

        <Text style={styles.tileDescription} numberOfLines={2}>
          {item.description}
        </Text>
      </Pressable>
    </View>
  );
};

ResourceTileBase.displayName = 'ResourceTile';

export const ResourceTile = memo(ResourceTileBase);

const styles = StyleSheet.create({
  tile: {
    flex: 1,
    minHeight: 160,
    borderRadius: 12,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    padding: 16,
  },
  tileHovered: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.HOVER_PRIMARY,
  },
  tileIconRow: {
    flexDirection: 'row',
    width: '100%',
  },
  tileIconCircle: {
    borderRadius: 9999,
    backgroundColor: '#1A1A1C',
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tileTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 10,
  },
  tileTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 16,
    fontWeight: '600',
    flexShrink: 1,
  },
  countPill: {
    backgroundColor: '#2D2D31',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 9999,
  },
  countPillText: {
    color: '#C9CDD2',
    fontSize: 12,
    fontWeight: '600',
  },
  tileDescription: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
    marginTop: 4,
  },
});


